<template>
  <div class="approval-sla-addon">
    <div class="addon-head">
      <div>
        <span>HRM_APPROVAL_SLA_INSIGHT</span>
        <h2>审批积压与 SLA 洞察</h2>
        <p>基于请假审批创建与处理时间，识别超 SLA 待审、慢处理记录、部门审批积压和月度处理效率。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="16" class="metric-row">
      <el-col v-for="item in metrics" :key="item.label" :xs="24" :sm="12" :lg="6">
        <div class="metric-card" :class="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.meta }}</em>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">部门 SLA 风险排行</div>
          <el-table :data="overview.deptSlaRanking" size="small" v-loading="loading">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="approval_count" label="审批" width="80" />
            <el-table-column prop="pending_count" label="待审" width="80" />
            <el-table-column prop="overdue_pending_count" label="超时待审" width="100" />
            <el-table-column prop="avg_handled_hours" label="均时" width="80" />
            <el-table-column prop="sla_pass_rate" label="达标率%" width="90" />
            <el-table-column prop="risk_score" label="风险分" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">风险信号分布</div>
          <div class="status-list">
            <div v-for="item in overview.riskSignalDistribution" :key="item.signal_name" class="status-item">
              <span>{{ item.signal_name }}</span>
              <strong>{{ item.signal_count }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">超 SLA 待审</div>
          <el-table :data="overview.overduePendingList" size="small" v-loading="loading" empty-text="暂无超时待审">
            <el-table-column prop="staff_name" label="员工" width="110" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="type_num" label="类型" width="90">
              <template slot-scope="{ row }">{{ formatLeaveType(row.type_num) }}</template>
            </el-table-column>
            <el-table-column prop="days" label="天数" width="70" />
            <el-table-column prop="pending_hours" label="待审小时" width="100" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">慢处理记录</div>
          <el-table :data="overview.slowHandledList" size="small" v-loading="loading" empty-text="暂无慢处理记录">
            <el-table-column prop="staff_name" label="员工" width="110" />
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="status" label="状态" width="80">
              <template slot-scope="{ row }">{{ formatStatus(row.status) }}</template>
            </el-table-column>
            <el-table-column prop="handled_hours" label="处理小时" width="100" />
            <el-table-column prop="days" label="天数" width="70" />
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">状态 SLA 分布</div>
          <div class="status-list">
            <div v-for="item in overview.statusSlaDistribution" :key="item.status" class="status-item">
              <span>{{ formatStatus(item.status) }}</span>
              <strong>{{ item.approval_count }}</strong>
              <em>违约 {{ item.breach_count || 0 }}</em>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">月度 SLA 趋势</div>
          <div class="trend-list">
            <div v-for="item in overview.monthlySlaTrend" :key="item.month_label" class="trend-item">
              <span>{{ item.month_label }}</span>
              <strong>{{ item.approval_count }}</strong>
              <em>待审 {{ item.pending_count || 0 }} / 已处理 {{ item.handled_count || 0 }} / 均时 {{ item.avg_handled_hours || 0 }}</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getApprovalSlaInsightOverview } from '../api/approvalSlaInsight'

export default {
  name: 'ApprovalSlaInsightOverview',
  data () {
    return {
      loading: false,
      overview: {
        slaHours: 48,
        approvalRequestCount: 0,
        pendingCount: 0,
        overduePendingCount: 0,
        avgHandledHours: 0,
        slaPassRate: 0,
        deptSlaRanking: [],
        overduePendingList: [],
        slowHandledList: [],
        statusSlaDistribution: [],
        monthlySlaTrend: [],
        riskSignalDistribution: []
      }
    }
  },
  computed: {
    metrics () {
      return [
        { label: '审批总量', value: this.overview.approvalRequestCount, meta: `SLA ${this.overview.slaHours || 48} 小时` },
        { label: '待审积压', value: this.overview.pendingCount, meta: '未处理审批', tone: 'orange' },
        { label: '超 SLA 待审', value: this.overview.overduePendingCount, meta: '需优先处理', tone: 'red' },
        { label: 'SLA 达标率', value: `${this.overview.slaPassRate || 0}%`, meta: `均时 ${this.overview.avgHandledHours || 0} 小时`, tone: 'blue' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      getApprovalSlaInsightOverview().then(response => {
        if (response.code === 200) {
          this.overview = Object.assign({}, this.overview, response.data || {})
        } else {
          this.$message.warning(response.message || '审批积压与 SLA 洞察加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    formatStatus (status) {
      const map = { 0: '未审核', 1: '通过', 2: '驳回', 3: '撤销' }
      return map[Number(status)] || '未知'
    },
    formatLeaveType (type) {
      const map = { 0: '事假', 1: '产假', 2: '病假', 3: '婚假', 4: '探亲假', 5: '陪产假' }
      return map[Number(type)] || '未知'
    }
  }
}
</script>

<style lang="less" scoped>
.approval-sla-addon {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.addon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.addon-head span {
  display: block;
  margin-bottom: 8px;
  color: #075985;
  font-size: 12px;
  font-weight: 700;
}

.addon-head h2 {
  margin: 0 0 8px;
  color: #17233d;
  font-size: 24px;
}

.addon-head p {
  margin: 0;
  color: #5f6f89;
}

.metric-row {
  margin-bottom: 16px;
}

.metric-card {
  min-height: 118px;
  padding: 18px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.metric-card span,
.metric-card em {
  display: block;
  color: #6b7a90;
  font-style: normal;
}

.metric-card strong {
  display: block;
  margin: 10px 0 8px;
  color: #17233d;
  font-size: 30px;
  line-height: 1;
}

.metric-card.red strong {
  color: #d93026;
}

.metric-card.orange strong {
  color: #d9822b;
}

.metric-card.blue strong {
  color: #2f6fec;
}

.lower-row {
  margin-top: 16px;
}

.status-list,
.trend-list {
  display: grid;
  gap: 10px;
}

.trend-list {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.status-item,
.trend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 6px;
}

.trend-item {
  align-items: flex-start;
  flex-direction: column;
}

.status-item span,
.status-item em,
.trend-item span,
.trend-item em {
  color: #5f6f89;
  font-style: normal;
}

.status-item strong,
.trend-item strong {
  color: #17233d;
  font-size: 16px;
}
</style>
